import type { UserListEntry } from "../../api/types";
import { APP_LANGUAGE_OPTIONS } from "../../i18n/languages";
import {
  hasAnyTeamAction,
  type TeamMemberPermissions,
} from "../../utils/teamMemberPermissions";
import {
  accountBadge,
  displayName,
  initials,
  tileMetaLine,
  tilePayLine,
  tileRoleLine,
  unconfirmedEmail,
} from "./teamLabels";

/**
 * G-145 — one colleague, one tile, at every width (it was the phone-only card
 * of G-102; the desktop table it stood in for is gone — the owner, 2026-10-13:
 * the sideways-scrolling table gave an "unfinished feeling of half built app").
 *
 * The tile says who the person is (name, what they do, where), what needs
 * doing (an invitation not used, an address not confirmed) and, in its footer,
 * the two things this page administers most (pay — owners only — and PIN /
 * contract). Every action is one tap away in `TeamMemberSheet`.
 *
 * The line under the name keeps G-106's ruling: the job title, falling back to
 * the role (`cardTitleLine`), now followed by the shop.
 */
export function TeamMemberCard({
  user,
  permissions,
  onOpen,
}: {
  user: UserListEntry;
  permissions: TeamMemberPermissions;
  onOpen: (user: UserListEntry) => void;
}) {
  // `user.language` is the server's spelling ("ES"), not the kiosk code ("es").
  const language = APP_LANGUAGE_OPTIONS.find((l) => l.value === user.language);
  /*
   * A viewer with no action on this person gets a tile that does not pretend
   * to be tappable — an ADMIN looking at a courier, for instance. The facts
   * still read the same; only the affordance goes.
   */
  const actionable = hasAnyTeamAction(permissions);
  const badge = accountBadge(user);
  const pay = tilePayLine(user, permissions.canEditPay);
  const roleLine = tileRoleLine(user);

  const body = (
    <>
      <span className="team-tile-head">
        <span className="team-tile-avatar" aria-hidden="true">
          {initials(user)}
        </span>
        <span className="team-tile-who">
          <span className="team-tile-name">{displayName(user)}</span>
          <span className="team-tile-role">{roleLine}</span>
        </span>
      </span>

      <span className="team-tile-badges">
        {!user.isActive ? <span className="badge muted">inactive</span> : null}
        {badge ? <span className={badge.className}>{badge.label}</span> : null}
        {unconfirmedEmail(user) ? (
          <span className="badge warning">Email not confirmed</span>
        ) : null}
      </span>

      <span className="team-tile-contact">
        <span className="team-tile-email">{user.email}</span>
        {user.phoneNumber ? <span className="muted">{user.phoneNumber}</span> : null}
      </span>

      <span className="team-tile-foot">
        <span>{pay ?? ""}</span>
        <span>{tileMetaLine(user, language?.label)}</span>
      </span>
    </>
  );

  if (!actionable) {
    return <div className="team-tile is-static">{body}</div>;
  }

  return (
    <button
      type="button"
      className="team-tile"
      onClick={() => onOpen(user)}
      aria-label={`${user.fullName}, ${roleLine} — open`}
    >
      {body}
    </button>
  );
}
